/**
 * Quantum selection page for the Quantik Mind frontend.
 *
 * This page is intentionally state-driven. It renders one clear experience for
 * each major state:
 * - empty: no signal source available yet
 * - ready: selection can run
 * - running: selection is in progress
 * - result: selection completed successfully
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScienceIcon from '@mui/icons-material/Science';
import CasinoIcon from '@mui/icons-material/Casino';
import HubIcon from '@mui/icons-material/Hub';
import GavelIcon from '@mui/icons-material/Gavel';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  CartesianGrid,
  Cell,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/authContext';
import { useProject } from '../context/ProjectContext';
import { useSubscription } from '../context/SubscriptionContext';
import {
  fetchSelectedFunctionalTests,
  fetchSelectedTests,
  isFunctionalAsyncQueuedResponse,
} from '../api/selectTest';
import { fetchRunStatus } from '../api/runs';
import type { BusinessRiskMetrics, RunLifecycleStatus } from '../api/runs';
import { formatPercent } from '../utils/businessRiskMetrics';
import { api, AuthError } from '../utils/apiClient';
import { formatApiError } from '../utils/formatApiError';
import ActionButton from '../components/common/ActionButton';
import PersistedNearMissLinks from '../components/results/PersistedNearMissLinks';
import SupportRequestModal from '../components/SupportRequestModal';
import { useSelectionProgress } from '../features/selection/hooks/useSelectionProgress';
import { useSelectionReadiness } from '../features/selection/hooks/useSelectionReadiness';
import {
  deriveSelectionPageState,
  EXECUTION_STEPS,
  formatPlanUsage,
  getStructuredError,
  inferPrinciple,
  type FunctionalSignalState,
  type FunctionalWhyTheseTests,
  type PageState,
  type SelectionMode,
  type SignalOverride,
} from '../features/selection/utils/selectionFormatting';
import type { Exp } from '../types';

type AsyncRunUiStatus = RunLifecycleStatus | 'timeout' | null;

export const canonicalizeFunctionalBusinessMetrics = (data: any): BusinessRiskMetrics | null => {
  const metrics = { ...(data?.business_metrics ?? {}) } as BusinessRiskMetrics & {
    total_tests?: number;
    selected_tests?: number;
  };
  const totalTests = Number(
    data?.counts?.total ?? data?.total_tests ?? metrics.total_tests
  );
  const selectedTests = Number(
    data?.counts?.selected ?? data?.selected_tests ?? metrics.selected_tests
  );
  const riskTotal = Number(data?.risk_total ?? metrics.total_risk);
  const selectedRisk = Number(data?.selected_risk_sum ?? metrics.selected_risk);

  if (Number.isFinite(totalTests)) {
    metrics.total_tests = totalTests;
  }
  if (Number.isFinite(selectedTests)) {
    metrics.selected_tests = selectedTests;
  }
  if (Number.isFinite(totalTests) && Number.isFinite(selectedTests) && totalTests > 0) {
    metrics.test_budget = (selectedTests / totalTests) * 100;
    metrics.tests_reduced = 100 * (1 - selectedTests / totalTests);
  }
  if (Number.isFinite(riskTotal)) {
    metrics.total_risk = riskTotal;
  }
  if (Number.isFinite(selectedRisk)) {
    metrics.selected_risk = selectedRisk;
  }
  const riskCoverage = Number(data?.risk_coverage ?? metrics.risk_coverage);
  if (Number.isFinite(riskCoverage)) {
    metrics.risk_coverage = riskCoverage;
  } else if (Number.isFinite(riskTotal) && Number.isFinite(selectedRisk) && riskTotal > 0) {
    metrics.risk_coverage = (selectedRisk / riskTotal) * 100;
  }
  if (typeof metrics.risk_coverage === 'number') {
    metrics.residual_risk = Math.max(0, 100 - metrics.risk_coverage);
  }
  const riskEfficiency = Number(data?.risk_efficiency ?? metrics.risk_efficiency);
  if (Number.isFinite(riskEfficiency)) {
    metrics.risk_efficiency = riskEfficiency;
  } else if (
    typeof metrics.risk_coverage === 'number' &&
    typeof metrics.test_budget === 'number' &&
    metrics.test_budget > 0
  ) {
    metrics.risk_efficiency = metrics.risk_coverage / metrics.test_budget;
  }
  const highRiskCoverage =
    data?.high_risk_coverage ?? metrics.high_risk_coverage ?? metrics.high_captured_pct;
  if (typeof highRiskCoverage === 'number' && Number.isFinite(highRiskCoverage)) {
    metrics.high_risk_coverage = highRiskCoverage;
    metrics.high_captured_pct = highRiskCoverage;
    metrics.high_residual_pct = Math.max(0, 100 - highRiskCoverage);
  }
  const pair = (covered?: number | null) =>
    typeof covered === 'number' && Number.isFinite(covered)
      ? {
          covered_pct: Math.max(0, Math.min(100, covered)),
          uncovered_pct: Math.max(0, 100 - Math.max(0, Math.min(100, covered))),
        }
      : { covered_pct: undefined, uncovered_pct: undefined };
  metrics.risk_coverage_summary = metrics.risk_coverage_summary ?? {
    overall: pair(metrics.risk_coverage),
    high: pair(metrics.high_risk_coverage ?? metrics.high_captured_pct),
    medium: pair(metrics.medium_captured_pct),
    low: pair(metrics.low_captured_pct),
  };
  const dependencyAware = Number(
    data?.dependency_aware_tests_added ?? metrics.dependency_aware_tests_added
  );
  if (Number.isFinite(dependencyAware)) {
    metrics.dependency_aware_tests_added = dependencyAware;
  }
  const alwaysGreenSkipped = Number(
    data?.always_green_tests_skipped ?? metrics.always_green_tests_skipped
  );
  if (Number.isFinite(alwaysGreenSkipped)) {
    metrics.always_green_tests_skipped = alwaysGreenSkipped;
  }
  const redundantAvoided = Number(
    data?.redundant_tests_avoided ?? metrics.redundant_tests_avoided
  );
  if (Number.isFinite(redundantAvoided)) {
    metrics.redundant_tests_avoided = redundantAvoided;
  }

  return Object.keys(metrics).length ? metrics : null;
};

const getStepIcon = (text: string) => {
  if (text.includes('Superposition')) {
    return <CasinoIcon fontSize="small" sx={{ color: '#39ff14' }} />;
  }

  if (text.includes('Entanglement')) {
    return <HubIcon fontSize="small" sx={{ color: '#39ff14' }} />;
  }

  if (text.includes('Uncertainty')) {
    return <GavelIcon fontSize="small" sx={{ color: '#39ff14' }} />;
  }

  if (text.includes('Completed')) {
    return <DoneAllIcon fontSize="small" sx={{ color: '#39ff14' }} />;
  }

  return <ScienceIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.6)' }} />;
};

const SignalStatus: React.FC<{
  label: string;
  available: boolean;
}> = ({ label, available }) => (
  <Stack
    direction="row"
    spacing={1.25}
    alignItems="center"
    sx={{
      px: 1.5,
      py: 1.15,
      borderRadius: 2.25,
      bgcolor: 'rgba(255,255,255,0.03)',
      minWidth: 0,
    }}
  >
    {available ? (
      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#39ff14' }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.42)' }} />
    )}
    <Typography variant="body2" sx={{ color: available ? '#EAF8ED' : 'rgba(255,255,255,0.76)' }}>
      {available ? label : `No ${label.toLowerCase()}`}
    </Typography>
  </Stack>
);

const SelectionSurface: React.FC<{
  title: string;
  subtitle: string;
  meta?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ title, subtitle, meta, children, footer }) => (
  <Paper
    sx={{
      p: { xs: 2.5, md: 3.5 },
      borderRadius: 4,
      background:
        'linear-gradient(180deg, rgba(14, 18, 24, 0.98) 0%, rgba(9, 12, 18, 0.99) 100%)',
      boxShadow: '0 28px 70px rgba(0, 0, 0, 0.34)',
      minWidth: 0,
    }}
  >
    <Stack spacing={3}>
      <Stack spacing={1}>
        {meta && (
          <Typography
            variant="overline"
            sx={{
              color: 'rgba(255,255,255,0.52)',
              letterSpacing: '0.12em',
              fontWeight: 700,
            }}
          >
            {meta}
          </Typography>
        )}
        <Typography variant="h4" sx={{ fontWeight: 750, lineHeight: 1.1 }}>
          {title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.66)',
            maxWidth: 760,
            lineHeight: 1.75,
          }}
        >
          {subtitle}
        </Typography>
      </Stack>

      {children}

      {footer && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
          {footer}
        </>
      )}
    </Stack>
  </Paper>
);

const ActionGroup: React.FC<{
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  tertiary?: React.ReactNode;
}> = ({ primary, secondary, tertiary }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={1.25}
    alignItems={{ xs: 'stretch', sm: 'center' }}
    flexWrap="wrap"
  >
    {primary}
    {secondary}
    {tertiary}
  </Stack>
);

const ResultMetric: React.FC<{
  label: string;
  value: string | number;
  helper: string;
  tooltip: string;
  color?: string;
}> = ({ label, value, helper, tooltip, color = '#E6EDF3' }) => (
  <Tooltip title={tooltip} arrow>
    <Paper
      tabIndex={0}
      sx={{
        p: 1.6,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.035)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: 'none',
        minWidth: 0,
        height: '100%',
      }}
    >
      <Stack spacing={0.8} sx={{ height: '100%', minHeight: 132 }}>
        <Stack direction="row" spacing={0.6} alignItems="flex-start" sx={{ minHeight: 34 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.52)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontWeight: 700,
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {label}
          </Typography>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', mt: 0.15, flexShrink: 0 }} />
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 740, color, lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.56)', lineHeight: 1.45, mt: 'auto' }}>
          {helper}
        </Typography>
      </Stack>
    </Paper>
  </Tooltip>
);

const coverageValue = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.min(100, value))
    : null;

const RiskCoverageBar: React.FC<{
  title: string;
  covered?: number | null;
  uncovered?: number | null;
  color: string;
  helper?: string;
}> = ({ title, covered, uncovered, color, helper }) => (
  <Stack spacing={0.7}>
    <Typography variant="caption" sx={{ color: '#E6EDF3', fontWeight: 700 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden' }}>
      <Box sx={{ width: `${coverageValue(covered) ?? 0}%`, bgcolor: color }} />
      <Box
        sx={{
          width: `${coverageValue(uncovered) ?? 0}%`,
          bgcolor: 'rgba(148, 163, 184, 0.36)',
        }}
      />
    </Box>
    <Stack direction="row" spacing={2} flexWrap="wrap">
      <Typography variant="caption" sx={{ color }}>
        Selected score mass: {formatPercent(coverageValue(covered))}
      </Typography>
      <Typography variant="caption" sx={{ color: '#F87171' }}>
        Unselected score mass: {formatPercent(coverageValue(uncovered))}
      </Typography>
    </Stack>
    {helper && (
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)' }}>
        {helper}
      </Typography>
    )}
  </Stack>
);

const ReadySignalNotice: React.FC<{
  state: FunctionalSignalState | null;
  mode: SelectionMode;
  loading?: boolean;
  error?: unknown;
  diagnostics?: Record<string, unknown>;
  onRetry?: () => void;
}> = ({ state, mode, loading = false, error = null, diagnostics = {}, onRetry }) => {
  if (loading) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.7 }}>
        Checking historical and runtime signal readiness…
      </Typography>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={onRetry ? <ActionButton onClick={onRetry}>Retry</ActionButton> : undefined}
      >
        Runtime readiness could not be loaded. Selection is unavailable until the server responds.
      </Alert>
    );
  }

  if (state === 'historical_only') {
    const runtimeConfigured = diagnostics.runtime_metrics_enabled === true;
    const reason = String(
      diagnostics.readiness_reason || diagnostics.acquisition_reason || diagnostics.failure_code || ''
    );
    const runtimeDetail = reason === 'inventory_prometheus_scope_missing'
      ? 'The saved Prometheus connection is ready, but its job is missing from the explicit runtime inventory.'
      : 'The saved runtime configuration did not return usable metrics for this decision.';
    return (
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.7 }}>
        {runtimeConfigured
          ? `Historical evidence is available. ${runtimeDetail}`
          : mode === 'functional'
          ? 'Running with historical evidence only. Configure observability to add runtime signals.'
          : 'Running with historical experiment evidence only. Configure observability to add runtime signals.'}
      </Typography>
    );
  }

  if (state === 'runtime_only') {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.7 }}>
        {mode === 'functional'
          ? 'Running in runtime mode. Add historical executions to improve learning over time.'
          : 'Running in runtime mode. Add historical experiment executions to strengthen learning over time.'}
      </Typography>
    );
  }

  if (state === 'full') {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.7 }}>
        {mode === 'functional'
          ? 'Full signal mode. Historical learning and runtime signals are both active.'
          : 'Full signal mode. Historical experiment learning and runtime signals are both active.'}
      </Typography>
    );
  }

  return null;
};

const ProgressList: React.FC<{
  currentStep: number;
  done: boolean;
}> = ({ currentStep, done }) => (
  <Stack spacing={1.4}>
    {EXECUTION_STEPS.map((step, index) => {
      const completed =
        done && index === EXECUTION_STEPS.length - 1
          ? true
          : index < currentStep;
      const active = index === currentStep;

      return (
        <Stack
          key={step}
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            px: 1.25,
            py: 1.1,
            borderRadius: 2,
            bgcolor: active
              ? 'rgba(57, 255, 20, 0.07)'
              : completed
              ? 'rgba(255,255,255,0.03)'
              : 'transparent',
          }}
        >
          <Box sx={{ display: 'grid', placeItems: 'center' }}>{getStepIcon(step)}</Box>
          <Typography
            variant="body2"
            sx={{
              color: completed
                ? '#EAF8ED'
                : active
                ? '#39ff14'
                : 'rgba(255,255,255,0.62)',
              fontWeight: active ? 650 : 500,
            }}
          >
            {step}
          </Typography>
        </Stack>
      );
    })}
  </Stack>
);

const SignalHowItWorksDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="sm"
    PaperProps={{
      sx: {
        bgcolor: '#0D1117',
        backgroundImage:
          'linear-gradient(180deg, rgba(15, 20, 28, 0.98) 0%, rgba(8, 11, 18, 0.99) 100%)',
        borderRadius: 3,
      },
    }}
  >
    <DialogTitle sx={{ fontWeight: 700 }}>How signal-based selection works</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.8 }}>
          Quantik Mind prioritizes tests using signal, not just inventory. Historical
          executions reveal what has been unstable before, and observability adds live
          pressure from the system right now.
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.8 }}>
          A test library tells Quantik Mind what exists. Historical results and runtime
          signals tell it what matters next.
        </Typography>
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5 }}>
      <ActionButton onClick={onClose}>
        Close
      </ActionButton>
    </DialogActions>
  </Dialog>
);

const SHOW_CHAOS_TAB = false;
const FUNCTIONAL_STATUS_POLL_INTERVAL_MS = 2000;
const FUNCTIONAL_STATUS_POLL_MAX_ATTEMPTS = 60;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const QuantumSelectionPage = () => {
  const navigate = useNavigate();
  const { authData } = useAuth();
  const { currentProject } = useProject();
  const {
    subscription,
    loading: subscriptionLoading,
    error: subscriptionError,
    refreshSubscription,
  } =
    useSubscription();

  const [mode, setMode] = useState<SelectionMode>('functional');
  const [error, setError] = useState<string | null>(null);
  const [selectedExperiments, setSelectedExperiments] = useState<Exp[]>([]);
  const [lastRunId, setLastRunId] = useState<string | null>(null);
  const [lastSelectionWhy, setLastSelectionWhy] = useState<FunctionalWhyTheseTests | null>(null);
  const [lastBusinessMetrics, setLastBusinessMetrics] =
    useState<BusinessRiskMetrics | null>(null);
  const [lastLibraryMode, setLastLibraryMode] = useState<'sample' | 'real' | null>(null);
  const [asyncRunStatus, setAsyncRunStatus] = useState<AsyncRunUiStatus>(null);
  const [asyncRunMessage, setAsyncRunMessage] = useState<string | null>(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [signalHelpOpen, setSignalHelpOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [signalOverride, setSignalOverride] = useState<SignalOverride>('auto');
  const [selectionTau, setSelectionTau] = useState(0.5);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const {
    running,
    done,
    currentStep,
    startProgress,
    playUntilSelectionRequest,
    completeProgress,
    resetProgress,
    stopProgress,
  } = useSelectionProgress();
  const {
    signalReadiness,
    setSignalReadiness,
    loadSignalReadiness,
    loading: signalReadinessLoading,
    error: signalReadinessError,
  } = useSelectionReadiness({
    projectId: currentProject?.id,
    mode,
    signalOverride,
  });
  const isMountedRef = useRef(true);
  const selectionRequestRef = useRef(0);
  const selectionScope = `${currentProject?.id || ''}|${mode}|${selectionTau}`;
  const selectionScopeRef = useRef(selectionScope);
  selectionScopeRef.current = selectionScope;

  useEffect(() => {
    selectionRequestRef.current += 1;
    setSelectedExperiments([]);
    setLastRunId(null);
    setLastSelectionWhy(null);
    setLastBusinessMetrics(null);
    setLastLibraryMode(null);
    setAsyncRunStatus(null);
    setAsyncRunMessage(null);
    setError(null);
    resetProgress();
    stopProgress();
  }, [selectionScope]);

  const chaosAllowed = !!subscription?.features.chaos_selection;
  const isChaosLocked = !subscriptionLoading && !chaosAllowed;
  const isInternalAdmin = !!authData?.isInternalAdmin;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);


  const pageState: PageState = useMemo(
    () => deriveSelectionPageState({ done, running, signalReadiness }),
    [done, running, signalReadiness]
  );

  const modeLabel = mode === 'functional' ? 'Functional Testing' : 'Chaos Engineering';
  const usageLabel = formatPlanUsage(
    subscription?.plan_code,
    subscription?.test_selections_current,
    subscription?.test_selections_limit
  );
  const isFreeEvaluation =
    subscription?.public_plan_code === 'free' || subscription?.plan_code === 'free';
  const monthlySelectionLimitReached =
    !!subscription &&
    isFreeEvaluation &&
    typeof subscription.test_selections_limit === 'number' &&
    subscription.test_selections_current >= subscription.test_selections_limit;
  const selectionLaunchBlocked =
    subscriptionLoading || !!subscriptionError || monthlySelectionLimitReached ||
    signalReadinessLoading || !!signalReadinessError || !signalReadiness;

  const chartData = useMemo(
    () => [
      {
        type: 'Superposition',
        Count: selectedExperiments.filter(
          (test: Exp) => inferPrinciple(test) === 'superposition'
        ).length,
      },
      {
        type: 'Entanglement',
        Count: selectedExperiments.filter(
          (test: Exp) => inferPrinciple(test) === 'entanglement'
        ).length,
      },
      {
        type: 'Uncertainty',
        Count: selectedExperiments.filter(
          (test: Exp) => inferPrinciple(test) === 'uncertainty'
        ).length,
      },
    ],
    [selectedExperiments]
  );

  const pollFunctionalRunStatus = async (runId: string, isCurrent: () => boolean) => {
    for (let attempt = 0; attempt < FUNCTIONAL_STATUS_POLL_MAX_ATTEMPTS; attempt += 1) {
      if (!isCurrent()) {
        return;
      }

      const status = await fetchRunStatus(runId);

      if (!isCurrent()) {
        return;
      }

      setAsyncRunStatus(status.status);

      if (status.status === 'completed') {
        setAsyncRunMessage('Selection completed. Opening results.');
        await completeProgress();
        stopProgress();
        await refreshSubscription();
        await loadSignalReadiness();
        if (!isCurrent()) return;
        navigate(`/results?runId=${runId}`, {
          state: { defaultTab: 'functional' },
        });
        return;
      }

      if (status.status === 'failed') {
        setError(status.error_message || 'Functional selection failed.');
        setAsyncRunMessage(null);
        resetProgress();
        stopProgress();
        return;
      }

      if (status.status === 'cancelled') {
        setError('Functional selection was cancelled.');
        setAsyncRunMessage(null);
        resetProgress();
        stopProgress();
        return;
      }

      setAsyncRunMessage(
        status.status === 'running'
          ? 'Selection is running in the worker.'
          : 'Selection is queued for asynchronous execution.'
      );

      await sleep(FUNCTIONAL_STATUS_POLL_INTERVAL_MS);
      if (!isCurrent()) {
        return;
      }
    }

    if (!isCurrent()) {
      return;
    }

    setAsyncRunStatus('timeout');
    setAsyncRunMessage(null);
    setError('Selection is still running. Check the results page again shortly.');
    stopProgress();
  };

  const runQuantumSelection = async () => {
    if (!currentProject) return;

    if (signalReadinessLoading || signalReadinessError || !signalReadiness) {
      return;
    }

    if (mode === 'chaos' && isChaosLocked) {
      return;
    }

    if (signalReadiness?.state === 'insufficient_signal') {
      return;
    }

    if (selectionLaunchBlocked) {
      return;
    }

    const requestId = ++selectionRequestRef.current;
    const requestedScope = selectionScope;
    const isCurrent = () => isMountedRef.current && selectionRequestRef.current === requestId && selectionScopeRef.current === requestedScope;
    startProgress();
    setError(null);
    setSelectedExperiments([]);
    setLastRunId(null);
    setLastSelectionWhy(null);
    setLastBusinessMetrics(null);
    setLastLibraryMode(null);
    setAsyncRunStatus(null);
    setAsyncRunMessage(null);

    try {
      await playUntilSelectionRequest();
      if (!isCurrent()) return;

      const data =
        mode === 'chaos'
          ? await fetchSelectedTests(currentProject.id, { signal_override: signalOverride })
          : await fetchSelectedFunctionalTests(currentProject.id, signalOverride, selectionTau);

      if (!isCurrent()) return;
      if (mode === 'functional' && isFunctionalAsyncQueuedResponse(data as any)) {
        const asyncResponse = data as any;
        setLastRunId(asyncResponse.run_id);
        setAsyncRunStatus(asyncResponse.status);
        setAsyncRunMessage(asyncResponse.message || 'Functional selection run queued.');
        await pollFunctionalRunStatus(asyncResponse.run_id, isCurrent);
        return;
      }

      const tests: Exp[] =
        mode === 'chaos'
          ? ((data as any)?.selected_tests ?? (data as any)?.selected ?? [])
          : ((data as any)?.selected ?? []).map((test: any) => ({
              ...test,
              quantum_origin: test.final_origin,
            }));

      setSelectedExperiments(tests);
      setLastRunId((data as any)?.run_id ?? null);
      setLastSelectionWhy(((data as any)?.why_these_tests as FunctionalWhyTheseTests) ?? null);
      setLastBusinessMetrics(
        mode === 'functional'
          ? canonicalizeFunctionalBusinessMetrics(data)
          : (((data as any)?.business_metrics as BusinessRiskMetrics) ?? null)
      );
      setLastLibraryMode((((data as any)?.library_mode as 'sample' | 'real' | undefined) ?? null));
      await refreshSubscription();
      await loadSignalReadiness();

      if (!isCurrent()) return;
      await completeProgress();
    } catch (err) {
      if (!isCurrent()) return;
      if (err instanceof AuthError) {
        return;
      }

      const structuredError = getStructuredError(err);
      if (structuredError?.code === 'insufficient_signal' || structuredError?.code === 'sample_limit_reached') {
        resetProgress();
        setSelectedExperiments([]);
        setLastRunId(null);
        setLastSelectionWhy(null);
        setLastBusinessMetrics(null);
        setLastLibraryMode(null);
        setError(null);
        setSignalReadiness({
          state: 'insufficient_signal',
          has_test_library:
            structuredError.has_test_library === undefined
              ? undefined
              : !!structuredError.has_test_library,
          has_sample_library:
            structuredError.has_sample_library === undefined
              ? undefined
              : !!structuredError.has_sample_library,
          has_real_library:
            structuredError.has_real_library === undefined
              ? undefined
              : !!structuredError.has_real_library,
          has_experiment_library:
            structuredError.has_experiment_library === undefined
              ? undefined
              : !!structuredError.has_experiment_library,
          has_historical_signal: !!structuredError.has_historical_signal,
          has_runtime_signal: !!structuredError.has_runtime_signal,
          sample_selection_count:
            typeof structuredError.sample_selection_count === 'number'
              ? structuredError.sample_selection_count
              : undefined,
          sample_selection_limit:
            typeof structuredError.sample_selection_limit === 'number'
              ? structuredError.sample_selection_limit
              : undefined,
          sample_limit_reached: structuredError.code === 'sample_limit_reached',
          signal_override: String(structuredError.signal_override || signalOverride),
          effective_mode: String(
            structuredError.effective_mode || structuredError.signal_state || 'insufficient_signal'
          ),
        });
        return;
      }

      setError(formatApiError(err, 'Selection failed.'));
    } finally {
      if (isCurrent()) stopProgress();
    }
  };

  const handleSeedDemoHistory = async () => {
    if (!currentProject) return;

    setSeedLoading(true);
    setError(null);

    try {
      await api.post('/functional/results/generate-history', {
        project_id: currentProject.id,
        days: 30,
        min_sessions_per_day: 2,
        max_sessions_per_day: 4,
        seed: 7,
      });

      await loadSignalReadiness();
    } catch (err) {
      const structuredError = getStructuredError(err);
      if (structuredError?.code === 'demo_history_precondition_failed') {
        const precondition =
          typeof structuredError.precondition === 'string'
            ? structuredError.precondition
            : null;
        setError(
          [
            'Sample execution history could not be generated.',
            precondition,
          ]
            .filter(Boolean)
            .join(' ')
        );
      } else {
        setError(formatApiError(err, 'Sample execution history could not be generated.'));
      }
    } finally {
      setSeedLoading(false);
    }
  };

  const renderEmptyState = () => (
    <SelectionSurface
      title={
        mode === 'functional'
          ? signalReadiness?.sample_limit_reached
            ? 'Connect your real test library to continue'
            : "You're one step away from intelligent test selection"
          : "You're one step away from intelligent chaos prioritization"
      }
      subtitle={
        mode === 'functional'
          ? signalReadiness?.sample_limit_reached
            ? 'You’ve completed the sample setup flow. To get meaningful recommendations, upload your real test library.'
            : 'Quantik Mind needs at least one signal to decide what should run next.'
          : 'Quantik Mind needs an experiment library and at least one signal to decide which chaos experiments should run next.'
      }
      footer={
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.54)' }}>
            {mode === 'functional'
              ? signalReadiness?.sample_limit_reached
                ? 'Sample setup is useful for exploration, but production-relevant recommendations require your real test library.'
                  : 'Test library alone is not enough to drive risk-aware selection.'
              : 'Experiment library inventory is required, but it is not enough on its own to drive risk-aware chaos prioritization.'}
          </Typography>
          <ActionButton
            disabled
            sx={{
              alignSelf: { xs: 'stretch', sm: 'center' },
            }}
          >
            Waiting for signal
          </ActionButton>
        </Stack>
      }
    >
      <Stack spacing={2.25}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.25}
          sx={{ maxWidth: 720 }}
        >
          {mode === 'chaos' && (
            <SignalStatus
              label="Experiment library"
              available={!!signalReadiness?.has_experiment_library}
            />
          )}
          {mode === 'functional' && (
            <SignalStatus
              label="Test library"
              available={!!signalReadiness?.has_test_library}
            />
          )}
          <SignalStatus
            label={
              mode === 'functional'
                ? 'Historical execution data'
                : 'Historical experiment data'
            }
            available={!!signalReadiness?.has_historical_signal}
          />
          <SignalStatus
            label="Runtime signals"
            available={!!signalReadiness?.has_runtime_signal}
          />
        </Stack>

        <ActionGroup
          primary={
            mode === 'functional' ? (
              <ActionButton
                onClick={() =>
                  signalReadiness?.sample_limit_reached
                    ? navigate('/settings', { state: { initialTab: 3 } })
                    : void handleSeedDemoHistory()
                }
                disabled={signalReadiness?.sample_limit_reached ? false : seedLoading}
                startIcon={seedLoading ? <CircularProgress size={16} sx={{ color: '#04110A' }} /> : <PlayArrowIcon />}
              >
                {signalReadiness?.sample_limit_reached
                    ? 'Upload real test library'
                    : seedLoading
                    ? 'Preparing sample execution history'
                    : 'Generate sample execution history'}
                </ActionButton>
            ) : (
              <ActionButton
                onClick={() => navigate('/settings', { state: { initialTab: 3 } })}
              >
                Configure experiment library
              </ActionButton>
            )
          }
          secondary={
            <ActionButton
              onClick={() =>
                signalReadiness?.sample_limit_reached
                  ? navigate('/onboarding')
                  : navigate(
                      mode === 'functional'
                        ? '/settings?tab=historical-data&type=functional'
                        : '/settings?tab=historical-data'
                    )
              }
            >
              {signalReadiness?.sample_limit_reached
                ? 'View API integration guide'
                : mode === 'functional'
                  ? 'Upload historical data'
                  : 'Upload historical data'}
            </ActionButton>
          }
          tertiary={
            signalReadiness?.sample_limit_reached ? undefined : (
              <ActionButton
                onClick={() => navigate('/settings', { state: { initialTab: 2 } })}
              >
                Connect observability
              </ActionButton>
            )
          }
        />

        <ActionButton
          onClick={() => setSignalHelpOpen(true)}
          endIcon={<ArrowOutwardIcon sx={{ fontSize: 16 }} />}
          sx={{
            alignSelf: 'flex-start',
          }}
        >
          How signal-based selection works
        </ActionButton>
      </Stack>
    </SelectionSurface>
  );

  const renderReadyState = () => (
    <SelectionSurface
      title={
        mode === 'functional'
          ? 'Ready for intelligent test selection'
          : 'Ready to prioritize chaos experiments'
      }
      subtitle={
        mode === 'functional'
          ? 'Run a selection to identify the tests most worth executing next.'
          : 'Run a selection to prioritize the most meaningful experiments for this system state.'
      }
      meta={mode === 'functional' ? 'Selection State' : 'Experiment State'}
      footer={
        <ActionGroup
          primary={
            <ActionButton
              disabled={running || selectionLaunchBlocked}
              onClick={() => void runQuantumSelection()}
              startIcon={<PlayArrowIcon />}
            >
              {subscriptionLoading ? 'Checking capacity' : 'Run Selection'}
            </ActionButton>
          }
          secondary={
            monthlySelectionLimitReached ? (
              <ActionButton onClick={() => setSupportModalOpen(true)}>
                Contact Support
              </ActionButton>
            ) : undefined
          }
        />
      }
    >
      <Stack spacing={2.5}>
        {mode === 'functional' && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              maxWidth: 560,
              bgcolor: 'rgba(255,255,255,0.025)',
              borderColor: 'rgba(255,255,255,0.12)',
            }}
          >
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">Selection breadth</Typography>
                <Tooltip title="Adaptive parameter, not a probability cutoff or guaranteed reduction. Lower values are more restrictive; the resulting size also depends on the model-probability distribution and coverage guardrails.">
                  <InfoOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                </Tooltip>
              </Stack>
              <Select
                size="small"
                value={selectionTau}
                onChange={(event) => setSelectionTau(Number(event.target.value))}
                inputProps={{ 'aria-label': 'Selection breadth' }}
                sx={{ width: 150 }}
              >
                <MenuItem value={0.5}>0.50 (default)</MenuItem>
                <MenuItem value={0.4}>0.40</MenuItem>
                <MenuItem value={0.3}>0.30</MenuItem>
                <MenuItem value={0.2}>0.20</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary">
                Effective default: 0.50. Lower values make selection more restrictive; actual reduction is not fixed.
              </Typography>
            </Stack>
          </Paper>
        )}
        {subscriptionError && (
          <Alert
            severity="error"
            sx={{
              borderRadius: 2.5,
              bgcolor: 'rgba(120, 18, 18, 0.18)',
              border: '1px solid rgba(255, 120, 120, 0.18)',
            }}
          >
            {subscriptionError}
          </Alert>
        )}
        {monthlySelectionLimitReached && (
          <Alert
            severity="warning"
            action={
              <ActionButton onClick={() => setSupportModalOpen(true)}>
                Contact Support
              </ActionButton>
            }
            sx={{
              borderRadius: 2.5,
              bgcolor: 'rgba(255, 193, 7, 0.08)',
              color: '#FFF7DF',
              border: '1px solid rgba(255, 193, 7, 0.22)',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Monthly selection limit reached.
            </Typography>
            <Typography variant="body2">
              Free Evaluation includes 10 selections per month. Contact support to continue evaluating Quantik Mind.
            </Typography>
          </Alert>
        )}
        {mode === 'functional' ? (
          <>
            {signalReadiness?.has_sample_library && !signalReadiness?.has_real_library && (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2.5,
                  bgcolor: 'rgba(57,255,20,0.08)',
                  color: '#EAF8ED',
                  border: '1px solid rgba(57,255,20,0.16)',
                }}
              >
                  We preloaded a sample test library so you can try your first selection immediately.
                  This sample setup is for first exploration. Replace it with your real test library to get production-relevant results.
                </Alert>
              )}
            <ReadySignalNotice
              state={signalReadiness?.state || null}
              mode={mode}
              loading={signalReadinessLoading}
              error={signalReadinessError}
              diagnostics={signalReadiness?.runtime_diagnostics}
              onRetry={() => void loadSignalReadiness()}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <SignalStatus
                label="Test library"
                available={!!signalReadiness?.has_test_library}
              />
              <SignalStatus
                label="Historical execution data"
                available={!!signalReadiness?.has_historical_signal}
              />
              <SignalStatus
                label="Runtime signals"
                available={!!signalReadiness?.has_runtime_signal}
              />
            </Stack>
          </>
        ) : (
          <>
            <ReadySignalNotice
              state={signalReadiness?.state || null}
              mode={mode}
              loading={signalReadinessLoading}
              error={signalReadinessError}
              diagnostics={signalReadiness?.runtime_diagnostics}
              onRetry={() => void loadSignalReadiness()}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <SignalStatus
                label="Experiment library"
                available={!!signalReadiness?.has_experiment_library}
              />
              <SignalStatus
                label="Historical experiment data"
                available={!!signalReadiness?.has_historical_signal}
              />
              <SignalStatus
                label="Runtime signals"
                available={!!signalReadiness?.has_runtime_signal}
              />
            </Stack>
          </>
        )}
      </Stack>
    </SelectionSurface>
  );

  const renderRunningState = () => (
    <SelectionSurface
      title={
        mode === 'functional' && asyncRunStatus
          ? `Selection ${asyncRunStatus}`
          : mode === 'functional'
          ? 'Selection in progress'
          : 'Evaluating experiment priority'
      }
      subtitle={
        asyncRunMessage ||
        'Quantik Mind is analyzing the available signal and composing the next execution set.'
      }
      meta="Execution"
      footer={
        <ActionButton
          disabled
          startIcon={<CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.72)' }} />}
          sx={{
            alignSelf: 'flex-start',
          }}
        >
          {asyncRunStatus === 'queued'
            ? 'Selection queued'
            : asyncRunStatus === 'running'
            ? 'Selection running'
            : 'Selection in progress'}
        </ActionButton>
      }
    >
      <ProgressList currentStep={currentStep} done={done} />
    </SelectionSurface>
  );

  const renderResultState = () => {
    const totalCount = selectedExperiments.length;
    const isSampleMode = lastLibraryMode === 'sample';
    const modelProbabilities = selectedExperiments
      .map((row: any) => row.informative_probability)
      .filter((value: unknown): value is number => typeof value === 'number' && Number.isFinite(value));
    const maxModelProbability = modelProbabilities.length
      ? Math.max(...modelProbabilities) * 100
      : null;
    const minModelProbability = modelProbabilities.length
      ? Math.min(...modelProbabilities) * 100
      : null;
    const membership = lastBusinessMetrics?.membership_reconciliation;
    const coverageGapCount = lastBusinessMetrics?.coverage_gaps?.length;
    const riskCoverage =
      lastBusinessMetrics?.risk_coverage_summary?.overall ?? {
        covered_pct: lastBusinessMetrics?.risk_coverage,
        uncovered_pct:
          typeof lastBusinessMetrics?.risk_coverage === 'number'
            ? 100 - lastBusinessMetrics.risk_coverage
            : undefined,
      };
    const highRiskCoverage =
      lastBusinessMetrics?.risk_coverage_summary?.high ?? {
        covered_pct: lastBusinessMetrics?.high_risk_coverage,
        uncovered_pct:
          typeof lastBusinessMetrics?.high_risk_coverage === 'number'
            ? 100 - lastBusinessMetrics.high_risk_coverage
            : undefined,
      };
    const mediumRiskCoverage =
      lastBusinessMetrics?.risk_coverage_summary?.medium ?? {
        covered_pct: lastBusinessMetrics?.medium_captured_pct,
        uncovered_pct: lastBusinessMetrics?.medium_residual_pct,
      };
    const lowRiskCoverage =
      lastBusinessMetrics?.risk_coverage_summary?.low ?? {
        covered_pct: lastBusinessMetrics?.low_captured_pct,
        uncovered_pct: lastBusinessMetrics?.low_residual_pct,
      };

    return (
      <SelectionSurface
        title="Selection completed"
        subtitle={
          isSampleMode
            ? 'Your first selection used sample signals. Review why the simulated high-risk tests were chosen, then replace the sample test library with your real one.'
            : 'Your next execution set is ready. Review the managed risk and open the full metrics view for detail.'
        }
        meta="Result"
        footer={
          <ActionGroup
            primary={
              <ActionButton
                onClick={() =>
                  isSampleMode
                    ? navigate('/settings', { state: { initialTab: 3 } })
                    : lastRunId &&
                      navigate(`/results?runId=${lastRunId}`, {
                        state: { defaultTab: mode },
                      })
                }
                disabled={isSampleMode ? false : !lastRunId}
            >
                {isSampleMode ? 'Upload your real test library' : 'View full metrics'}
              </ActionButton>
            }
            secondary={isSampleMode ? (
              <ActionButton
                onClick={() =>
                  lastRunId &&
                  navigate(`/results?runId=${lastRunId}`, {
                    state: { defaultTab: mode },
                  })
                }
                disabled={!lastRunId}
              >
                View full metrics
              </ActionButton>
            ) : undefined}
            tertiary={
              <ActionButton
                onClick={() => {
                  resetProgress();
                  setSelectedExperiments([]);
                  setLastRunId(null);
                  setLastSelectionWhy(null);
                  setLastBusinessMetrics(null);
                  setLastLibraryMode(null);
                  setAsyncRunStatus(null);
                  setAsyncRunMessage(null);
                }}
              >
                {isSampleMode ? 'Continue exploring with sample setup' : 'Run again'}
              </ActionButton>
            }
          />
        }
      >
        <Stack spacing={2.5}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(6, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            <ResultMetric
              label="Test reduction"
              value={formatPercent(lastBusinessMetrics?.tests_reduced)}
              helper="Test reduction"
              tooltip="Percentage of tests skipped from the original test set. This measures execution reduction, not risk elimination."
              color="#39ff14"
            />
            <ResultMetric
              label="Highest estimated informativeness"
              value={formatPercent(maxModelProbability)}
              helper={minModelProbability === null ? 'Model probability unavailable' : `Selected range ${formatPercent(minModelProbability)}–${formatPercent(maxModelProbability)}`}
              tooltip="Highest model-estimated chance that a selected test provides new, relevant risk evidence. This is a per-test maximum, not a probability for the whole selection."
            />
            <ResultMetric
              label="Critical risk coverage"
              value={formatPercent(lastBusinessMetrics?.mapped_risk_coverage?.critical?.covered_pct)}
              helper={`${lastBusinessMetrics?.mapped_risk_coverage?.critical?.covered_count ?? 'N/A'} / ${lastBusinessMetrics?.mapped_risk_coverage?.critical?.total_count ?? 'N/A'} defined critical risks`}
              tooltip="Coverage shows defined critical risks with at least one associated test selected. Tests have not necessarily run; this does not establish elimination of risk."
            />
            <ResultMetric
              label="Overall risk coverage"
              value={formatPercent(lastBusinessMetrics?.mapped_risk_coverage?.overall?.covered_pct)}
              helper={`${lastBusinessMetrics?.mapped_risk_coverage?.overall?.covered_count ?? 'N/A'} / ${lastBusinessMetrics?.mapped_risk_coverage?.overall?.total_count ?? 'N/A'} defined risks`}
              tooltip="Coverage shows defined risks with at least one associated test selected. Overlapping mappings are counted once."
            />
            <ResultMetric
              label="Risks without selected tests"
              value={typeof coverageGapCount === 'number' ? coverageGapCount : 'N/A'}
              helper="Defined risks with no associated selected test"
              tooltip="Defined risks not represented by any finally selected test. Open details for names, technical identifiers, severity, associated tests and threshold reasons."
            />
          </Box>

          {lastSelectionWhy && (
            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Stack spacing={1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Why this selection?
                </Typography>
                {[
                  membership
                    ? `${membership.at_or_above_threshold ?? 'N/A'} tests met the minimum informativeness for standard selection; ${membership.coverage_guardrail_additions ?? 'N/A'} were added by explicit coverage guardrails.`
                    : 'Membership details are unavailable for this historical run.',
                  `Critical risk coverage: ${lastBusinessMetrics?.mapped_risk_coverage?.critical?.covered_count ?? 'N/A'} / ${lastBusinessMetrics?.mapped_risk_coverage?.critical?.total_count ?? 'N/A'}.`,
                  `Overall risk coverage: ${lastBusinessMetrics?.mapped_risk_coverage?.overall?.covered_count ?? 'N/A'} / ${lastBusinessMetrics?.mapped_risk_coverage?.overall?.total_count ?? 'N/A'}; gaps: ${coverageGapCount ?? 'N/A'}.`,
                  'After membership, selected tests are ordered by estimated informative-evidence probability, with stable test ID ties.',
                ].map((bullet) => (
                  <Typography key={bullet} variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                    • {bullet}
                  </Typography>
                ))}
                {lastSelectionWhy.note && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)' }}>
                    {lastSelectionWhy.note}
                  </Typography>
                )}
              </Stack>
            </Paper>
          )}

          <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontWeight: 700 } }}>
            <Box component="summary">Selection details</Box>
            <Typography variant="body2" sx={{ mt: 1 }}><strong>Minimum informativeness for standard selection:</strong> {formatPercent(typeof membership?.effective_threshold === 'number' ? membership.effective_threshold * 100 : null)}. Tests meeting this estimated probability are selected. Additional tests may be included to cover important risks.</Typography>
            <Typography variant="body2"><strong>Selection breadth:</strong> {membership?.tau ?? 'Unavailable'}. Lower values make selection more restrictive. Coverage requirements can add tests. The value does not guarantee a selection percentage.</Typography>
          </Box>

          {lastBusinessMetrics?.model_details && (
            <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontWeight: 700 } }}>
              <Box component="summary">Model details</Box>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <Typography variant="body2">{lastBusinessMetrics.model_details.warning ?? 'Model provenance warning unavailable.'}</Typography>
                <Typography variant="caption">Model: {lastBusinessMetrics.model_details.model_version ?? 'Unavailable'}</Typography>
                <Typography variant="caption">Predictor: {lastBusinessMetrics.model_details.probability_source ?? 'Unavailable'}; training data: {lastBusinessMetrics.model_details.training_data_kind ?? 'Unavailable'}.</Typography>
                <Typography variant="caption">Calibration status: {lastBusinessMetrics.model_details.calibration_status ?? 'Unavailable'}. Real-world calibration has not been demonstrated.</Typography>
              </Stack>
            </Box>
          )}

          <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontWeight: 700 } }}>
            <Box component="summary">View legacy score diagnostics</Box>
          <Paper
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Legacy p_initial score-mass diagnostics
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)' }}>
                p_initial is a legacy heuristic selection weight. These bars show selected score mass, not RF probability, business-risk coverage or residual risk.
              </Typography>
              <RiskCoverageBar
                title="All p_initial score mass"
                covered={riskCoverage.covered_pct}
                uncovered={riskCoverage.uncovered_pct}
                color="#39ff14"
              />
              <RiskCoverageBar
                title="p_initial ≥ 0.70 score mass"
                covered={highRiskCoverage.covered_pct}
                uncovered={highRiskCoverage.uncovered_pct}
                color="#F87171"
              />
              <RiskCoverageBar
                title="0.40 ≤ p_initial &lt; 0.70 score mass"
                covered={mediumRiskCoverage.covered_pct}
                uncovered={mediumRiskCoverage.uncovered_pct}
                color="#FBBF24"
              />
              <RiskCoverageBar
                title="p_initial &lt; 0.40 score mass"
                covered={lowRiskCoverage.covered_pct}
                uncovered={lowRiskCoverage.uncovered_pct}
                color="#C9D1D9"
                helper="Legacy score-band diagnostic only."
              />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)' }}>
                Selected / unselected refers only to legacy score mass. The RF membership decision uses informative-evidence probability and explicit mapping guardrails.
              </Typography>
            </Stack>
          </Paper>
          </Box>

          <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontWeight: 700 } }}>
            <Box component="summary">View mapped coverage gaps and legacy classifications</Box>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {(lastBusinessMetrics?.coverage_gaps ?? []).map((gap) => (
                <Paper key={gap.proposition_id} variant="outlined" sx={{ p: 1.25, bgcolor: 'rgba(255,255,255,0.02)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                    {gap.proposition_name ?? gap.proposition_id}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                    Severity: {gap.criticality} · associated tests: {gap.associated_test_count} · highest saved probability: {formatPercent(typeof gap.highest_informative_probability === 'number' ? gap.highest_informative_probability * 100 : null)} · reason: {gap.exclusion_reason ?? 'unavailable'}
                  </Typography>
                  {gap.proposition_name && <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.45)', overflowWrap: 'anywhere' }}>Technical ID: {gap.proposition_id}</Typography>}
                </Paper>
              ))}
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)' }}>
                Legacy classifications are not RF membership causes: dependency-signal flags on selected tests {lastBusinessMetrics?.classification_diagnostics?.legacy_dependency_signal_selected_count ?? 'N/A'}; name-similarity flags on unselected tests {lastBusinessMetrics?.classification_diagnostics?.name_similarity_unselected_count ?? 'N/A'}; historically-stable flags on unselected tests {lastBusinessMetrics?.classification_diagnostics?.historically_stable_unselected_count ?? 'N/A'}. Name similarity does not establish behavioral equivalence.
              </Typography>
            </Stack>
          </Box>

          <Box
            component="details"
            sx={{
              '& summary': {
                cursor: 'pointer',
                color: 'rgba(255,255,255,0.72)',
                fontWeight: 700,
              },
            }}
          >
            <Box component="summary">View technical selection details</Box>
            {mode === 'functional' && <PersistedNearMissLinks experiments={selectedExperiments} />}
            <Box sx={{ width: '100%', height: 220, mt: 1.5 }}>
              <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 8, right: 24, left: 4, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255, 255, 255, 0.08)"
                  horizontal
                  vertical={false}
                />
                <XAxis
                  type="number"
                  stroke="rgba(255,255,255,0.36)"
                  style={{ fontSize: '0.75rem' }}
                />
                <YAxis
                  type="category"
                  dataKey="type"
                  width={132}
                  stroke="rgba(255,255,255,0.52)"
                  style={{ fontSize: '0.84rem', fontWeight: 600 }}
                />
                <ReTooltip
                  contentStyle={{
                    backgroundColor: '#121821',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '10px',
                    fontSize: '0.85rem',
                    padding: '10px 12px',
                  }}
                  labelStyle={{ color: '#E6EDF3', fontWeight: 600 }}
                  itemStyle={{ color: '#E6EDF3' }}
                  cursor={{ fill: 'rgba(255, 255, 255, 0.03)' }}
                />
                <Bar dataKey="Count" radius={[0, 8, 8, 0]} barSize={28}>
                  {chartData.map((_, index) => (
                    <Cell
                      key={index}
                      fill={
                        index === 0
                          ? 'rgba(57, 255, 20, 0.88)'
                          : index === 1
                          ? 'rgba(57, 255, 20, 0.62)'
                          : 'rgba(57, 255, 20, 0.38)'
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </Box>
          </Box>
        </Stack>
      </SelectionSurface>
    );
  };

  const renderCurrentState = () => {
    switch (pageState) {
      case 'empty':
        return renderEmptyState();
      case 'running':
        return renderRunningState();
      case 'result':
        return renderResultState();
      case 'ready':
      default:
        return renderReadyState();
    }
  };

  const renderAdminDiagnostics = () => {
    if (!isInternalAdmin) {
      return null;
    }

    return (
      <Paper
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.025)',
          boxShadow: 'none',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Admin diagnostics
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Internal-only controls for comparing signal-source behavior.
              </Typography>
            </Box>
            <ActionButton
              onClick={() => setDiagnosticsOpen((current) => !current)}
            >
              {diagnosticsOpen ? 'Hide' : 'Show'}
            </ActionButton>
          </Stack>

          {diagnosticsOpen && (
            <Stack spacing={1.5}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)' }}>
                Internal diagnostic mode. Use this to compare how selection changes when
                specific signal sources are excluded.
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Typography variant="body2" sx={{ minWidth: 110, color: 'rgba(255,255,255,0.72)' }}>
                  Signal override
                </Typography>
                <Select
                  size="small"
                  value={signalOverride}
                  onChange={(event) => setSignalOverride(event.target.value as SignalOverride)}
                  sx={{
                    minWidth: 220,
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.03)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.12)',
                    },
                  }}
                >
                  <MenuItem value="auto">Auto</MenuItem>
                  <MenuItem value="ignore_history">Ignore history</MenuItem>
                  <MenuItem value="ignore_observability">Ignore observability</MenuItem>
                  <MenuItem value="full">Require full signal</MenuItem>
                </Select>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
      }}
    >
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
        >
          <Typography variant="h4" sx={{ fontWeight: 760, letterSpacing: '-0.02em' }}>
            {modeLabel}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.62)',
              fontWeight: 500,
              textAlign: { xs: 'left', md: 'right' },
            }}
          >
            {usageLabel}
          </Typography>
        </Stack>

        <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Tabs
            value={mode}
            onChange={(_, value) => setMode(value)}
            sx={{
              minHeight: 0,
              '& .MuiTabs-indicator': {
                backgroundColor: '#39FF14',
                height: 2,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 46,
                px: 0,
                mr: 3.5,
                color: 'rgba(255,255,255,0.54)',
                fontWeight: 600,
                '&.Mui-selected': {
                  color: '#FFFFFF',
                },
              },
            }}
          >
            <Tab value="functional" disableRipple label="Functional" />
            {SHOW_CHAOS_TAB && (
              <Tab
                value="chaos"
                disableRipple
                label="Chaos"
                disabled={isChaosLocked}
                title={isChaosLocked ? 'Available on Team plan' : undefined}
                sx={{
                  opacity: isChaosLocked ? 0.4 : 1,
                  '&:hover': {
                    bgcolor: isChaosLocked ? 'transparent' : undefined,
                  },
                }}
              />
            )}
          </Tabs>
        </Box>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              borderRadius: 2.5,
              bgcolor: 'rgba(120, 18, 18, 0.18)',
              border: '1px solid rgba(255, 120, 120, 0.18)',
            }}
          >
            {error}
          </Alert>
        )}

        {renderAdminDiagnostics()}

        {renderCurrentState()}
      </Stack>

      <SignalHowItWorksDialog
        open={signalHelpOpen}
        onClose={() => setSignalHelpOpen(false)}
      />
      <SupportRequestModal
        open={supportModalOpen}
        requesterEmail={authData?.email}
        initialCategory="commercial_license"
        onClose={() => setSupportModalOpen(false)}
        onRequestCreated={() => setSupportModalOpen(false)}
      />
    </Box>
  );
};

export default QuantumSelectionPage;
